import {
  getProjectFinalAssets,
  createProjectFinalAsset,
  updateProjectFinalAsset,
  deleteProjectFinalAsset,
  getProjectWorkingFiles,
  createProjectWorkingFile,
  updateProjectWorkingFile,
  deleteProjectWorkingFile
} from '@/api/workflow';

export default {
  namespaced: true,
  state: () => ({
    finalAssets: {
      loading: false,
      error: null,
      items: [],
      projectId: null
    },
    workingFiles: {
      loading: false,
      error: null,
      items: [],
      projectId: null
    }
  }),
  mutations: {
    fetchFinalAssetsPending(state) {
      state.finalAssets.loading = true;
      state.finalAssets.error = null;
      state.finalAssets.items = [];
      state.finalAssets.projectId = null;
    },
    fetchFinalAssetsSuccess(state, { assets, projectId }) {
      state.finalAssets.loading = false;
      state.finalAssets.error = null;
      state.finalAssets.items = assets;
      state.finalAssets.projectId = projectId;
    },
    fetchFinalAssetsFailure(state, error) {
      state.finalAssets.loading = false;
      state.finalAssets.error = error;
    },
    setFinalAssetsError(state, error) {
      state.finalAssets.error = error;
    },
    addFinalAsset(state, asset) {
      state.finalAssets.items = [...state.finalAssets.items, asset];
    },
    setFinalAsset(state, asset) {
      state.finalAssets.items = state.finalAssets.items.map(item => {
        if (item.id === asset.id) {
          return asset;
        }
        return item;
      });
    },
    removeFinalAsset(state, asset) {
      state.finalAssets.items = state.finalAssets.items.filter(item => item.id !== asset.id);
    },
    fetchWorkingFilesPending(state) {
      state.workingFiles.loading = true;
      state.workingFiles.error = null;
      state.workingFiles.items = [];
      state.workingFiles.projectId = null;
    },
    fetchWorkingFilesSuccess(state, { assets, projectId }) {
      state.workingFiles.loading = false;
      state.workingFiles.error = null;
      state.workingFiles.items = assets;
      state.workingFiles.projectId = projectId;
    },
    fetchWorkingFilesFailure(state, error) {
      state.workingFiles.loading = false;
      state.workingFiles.error = error;
    },
    setWorkingFilesError(state, error) {
      state.workingFiles.error = error;
    },
    addWorkingFile(state, asset) {
      state.workingFiles.items = [...state.workingFiles.items, asset];
    },
    setWorkingFile(state, asset) {
      state.workingFiles.items = state.workingFiles.items.map(item => {
        if (item.id === asset.id) {
          return asset;
        }
        return item;
      });
    },
    removeWorkingFile(state, asset) {
      state.workingFiles.items = state.workingFiles.items.filter(item => item.id !== asset.id);
    }
  },
  actions: {
    async fetchCurrentProjectFinalAssets({ commit, state, rootState }) {
      if (state.finalAssets.projectId === rootState.workflow.currentProjectId) {
        return;
      }
      commit('fetchFinalAssetsPending');
      try {
        const payload = {
          projectId: rootState.workflow.currentProjectId
        };
        const assets = await getProjectFinalAssets(payload);
        commit('fetchFinalAssetsSuccess', { assets, projectId: payload.projectId });
      } catch (err) {
        console.error('Server error when getting final assets', err);
        commit('fetchFinalAssetsFailure', 'Error when getting final assets');
      }
    },
    async addCurrentProjectFinalAsset({ commit, rootState }, { asset, lastUpload }) {
      try {
        const payload = {
          projectId: rootState.workflow.currentProjectId,
          ...asset,
          lastUpload,
          fileId: asset.fileId || asset?.resumableFile?.uniqueIdentifier
        };
        const result = await createProjectFinalAsset(payload);
        commit('addFinalAsset', result);
      } catch (err) {
        console.error(`Server error when adding final asset`, err);
        throw err;
      }
    },
    replaceFinalAsset({ commit }, asset) {
      commit('setFinalAsset', asset);
    },
    async updateCurrentProjectFinalAsset({ commit, state, rootState }, { asset }) {
      const previousValue = state.finalAssets.items.find(item => item.id === asset.id);
      commit('setFinalAsset', asset);
      try {
        const payload = {
          projectId: rootState.workflow.currentProjectId,
          assetId: asset.id,
          ...asset
        };
        await updateProjectFinalAsset(payload);
      } catch (err) {
        console.error(`Server error when updating final asset`, err);
        commit('setFinalAsset', previousValue);
        commit('setFinalAssetsError', 'Error when updating final asset');
      }
    },
    async removeCurrentProjectFinalAsset({ commit, rootState }, { asset }) {
      commit('removeFinalAsset', asset);
      try {
        const payload = {
          projectId: rootState.workflow.currentProjectId,
          assetId: asset.id
        };
        await deleteProjectFinalAsset(payload);
      } catch (err) {
        console.error(`Server error when removing final asset`, err);
        commit('setFinalAssetsError', 'Error when removing final asset');
      }
    },
    async fetchCurrentProjectWorkingFiles({ commit, state, rootState }) {
      if (state.workingFiles.projectId === rootState.workflow.currentProjectId) {
        return;
      }
      commit('fetchWorkingFilesPending');
      try {
        const payload = {
          projectId: rootState.workflow.currentProjectId
        };
        const assets = await getProjectWorkingFiles(payload);
        commit('fetchWorkingFilesSuccess', { assets, projectId: payload.projectId });
      } catch (err) {
        console.error('Server error when getting working files', err);
        commit('fetchWorkingFilesFailure', 'Error when getting working files');
      }
    },
    async addCurrentProjectWorkingFile({ commit, rootState }, { asset }) {
      try {
        const payload = {
          projectId: rootState.workflow.currentProjectId,
          ...asset,
          fileId: asset.fileId || asset?.resumableFile?.uniqueIdentifier,
          userIds: asset.users.map(u => u.id)
        };
        const result = await createProjectWorkingFile(payload);
        commit('addWorkingFile', result);
      } catch (err) {
        console.error(`Server error when adding working file`, err);
        throw err;
      }
    },
    async updateCurrentProjectWorkingFile({ commit, state, rootState }, { asset }) {
      const previousValue = state.workingFiles.items.find(item => item.id === asset.id);
      commit('setWorkingFile', asset);
      try {
        const payload = {
          projectId: rootState.workflow.currentProjectId,
          assetId: asset.id,
          ...asset
        };
        await updateProjectWorkingFile(payload);
      } catch (err) {
        console.error(`Server error when updating working file`, err);
        commit('setWorkingFile', previousValue);
        commit('setWorkingFilesError', 'Error when updating working file');
      }
    },
    async updateCurrentProjectWorkingFileUsers({ commit, state, rootState }, { asset }) {
      const previousValue = state.workingFiles.items.find(item => item.id === asset.id);
      commit('setWorkingFile', asset);
      try {
        const payload = {
          projectId: rootState.workflow.currentProjectId,
          assetId: asset.id,
          userIds: asset.users.map(u => u.id)
        };
        await updateProjectWorkingFile(payload);
      } catch (err) {
        console.error(`Server error when updating working file users`, err);
        commit('setWorkingFile', previousValue);
        commit('setWorkingFilesError', 'Error when updating working file');
      }
    },
    async removeCurrentProjectWorkingFile({ commit, rootState }, { asset }) {
      commit('removeWorkingFile', asset);
      try {
        const payload = {
          projectId: rootState.workflow.currentProjectId,
          assetId: asset.id
        };
        await deleteProjectWorkingFile(payload);
      } catch (err) {
        console.error(`Server error when removing working file`, err);
        commit('setWorkingFilesError', 'Error when removing working file');
      }
    }
  }
};
